import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const FAQsTab = () => {
  const { studio, refreshUser } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [courses, setCourses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (studio?.faqs) {
      setFaqs(studio.faqs.map(f => ({
        question: f.question || '',
        answer: f.answer || '',
        appliesTo: f.appliesTo || 'all',
        courses: (f.courses || []).map(c => typeof c === 'object' ? c._id : c)
      })));
    }
  }, [studio?.faqs]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const { data } = await api.get('/courses?status=active');
        setCourses(data.courses || data || []);
      } catch {}
    };
    fetchCourses();
  }, []);

  const updateFaq = (index, field, value) => {
    const updated = [...faqs];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'appliesTo' && value === 'all') {
      updated[index].courses = [];
    }
    setFaqs(updated);
    setSaved(false);
  };

  const toggleCourse = (faqIndex, courseId) => {
    const updated = [...faqs];
    const current = updated[faqIndex].courses || [];
    if (current.includes(courseId)) {
      updated[faqIndex].courses = current.filter(id => id !== courseId);
    } else {
      updated[faqIndex].courses = [...current, courseId];
    }
    setFaqs(updated);
    setSaved(false);
  };

  const addFaq = () => {
    setFaqs([...faqs, { question: '', answer: '', appliesTo: 'all', courses: [] }]);
    setSaved(false);
  };

  const removeFaq = (index) => {
    setFaqs(faqs.filter((_, i) => i !== index));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put('/studio/faqs', { faqs });
      await refreshUser();
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save FAQs');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Enrollment FAQs</h2>
        <p className="text-sm text-gray-500 mb-6">Manage FAQs shown on your course enrollment pages. You can show each FAQ on all courses or only on selected ones.</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
            {error}
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 ml-2">&times;</button>
          </div>
        )}

        <div className="space-y-5">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <input
                  value={faq.question}
                  onChange={e => updateFaq(i, 'question', e.target.value)}
                  placeholder="Enter question"
                  className="flex-1 text-sm font-medium border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
                <button
                  onClick={() => removeFaq(i)}
                  className="text-gray-400 hover:text-red-500 p-1"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <textarea
                value={faq.answer}
                onChange={e => updateFaq(i, 'answer', e.target.value)}
                placeholder="Enter answer"
                rows={3}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent resize-none mb-3"
              />

              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-500 font-medium">Show on:</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name={`appliesTo-${i}`}
                    checked={faq.appliesTo === 'all'}
                    onChange={() => updateFaq(i, 'appliesTo', 'all')}
                    className="accent-brand-500"
                  />
                  <span className="text-gray-700">All courses</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name={`appliesTo-${i}`}
                    checked={faq.appliesTo === 'selected'}
                    onChange={() => updateFaq(i, 'appliesTo', 'selected')}
                    className="accent-brand-500"
                  />
                  <span className="text-gray-700">Selected courses</span>
                </label>
              </div>

              {faq.appliesTo === 'selected' && (
                <div className="mt-3 pl-1">
                  {courses.length === 0 ? (
                    <p className="text-xs text-gray-400">No active courses found.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {courses.map(course => (
                        <label key={course._id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={faq.courses?.includes(course._id)}
                            onChange={() => toggleCourse(i, course._id)}
                            className="accent-brand-500 rounded"
                          />
                          <span className="text-sm text-gray-700">{course.name}</span>
                          {course.danceStyle && (
                            <span className="text-xs text-gray-400">({course.danceStyle})</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={addFaq}
          className="mt-4 text-sm text-brand-500 hover:text-brand-600 font-medium"
        >
          + Add FAQ
        </button>

        <div className="mt-5 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
          >
            {saving ? 'Saving...' : 'Save FAQs'}
          </button>
          {saved && <span className="text-sm text-green-600 font-medium">Saved</span>}
        </div>
      </div>
    </div>
  );
};

export default FAQsTab;
